import React, { useRef, useState, useEffect } from "react";
import TimeMatchBoard from "./TimeMatchBoard";
import correctSound from "./sounds/correct.mp3";
import errorSound from "./sounds/error.mp3";
import fireworksData from "./animations/fireworks.json";
import GameOverScreen from "../GameOverScreen";
import fireworksSound from "./sounds/fireworks.mp3";
import GameLayoutTopbar from "../layouts/GameLayoutTopbar";
import { usePlayClock } from "../common/usePlayClock";
import "./TimeMatchForm.css";

import { startSession, endSession } from "../../services/GameService";
import {
  DEFAULT_TIME_PROGRESSION,
  targetTimeForStep,
} from "./timeProgression";

const AnalogToDigitalForm = ({ progression: progressionProp }) => {
  const progression =
    progressionProp && progressionProp.length
      ? progressionProp
      : DEFAULT_TIME_PROGRESSION;
  const [analogTime, setAnalogTime] = useState(new Date());
  const [clockTimes, setClockTimes] = useState([]);
  const [round, setRound] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [startOfRoundTime, setStartOfRoundTime] = useState(new Date());
  const [checks, setChecks] = useState([]);
  const [showFireworks, setShowFireworks] = useState(false);
  const [isGameReady, setIsGameReady] = useState(false);
  const [level, setLevel] = useState(0);
  const [numberOfClocks, setNumberOfClocks] = useState(0);
  const [showSeconds, setShowSeconds] = useState(false);
  const [is24Hour, setIs24Hour] = useState(false);

  const successfulChoicesRef = useRef(0);
  const correctSoundRef = useRef(new Audio(correctSound));
  const errorSoundRef = useRef(new Audio(errorSound));
  const fireworksAudioRef = useRef(new Audio(fireworksSound));

  const sessionIdRef = useRef(null);
  const scoreRef = useRef(0);
  const playClock = usePlayClock();

  const numberOfRoundsRef = useRef(0);
  const timeBetweenRoundsRef = useRef(0);

  useEffect(() => {
    const initGame = async () => {
      await startNewGame();
      setIsGameReady(true);
    };
    initGame();

    return () => {
      if (correctSoundRef.current) {
        correctSoundRef.current.pause();
        correctSoundRef.current.currentTime = 0;
      }
      if (errorSoundRef.current) {
        errorSoundRef.current.pause();
        errorSoundRef.current.currentTime = 0;
      }
      if (fireworksAudioRef.current) {
        fireworksAudioRef.current.pause();
        fireworksAudioRef.current.currentTime = 0;
      }
    };
  }, []);

  const startNewGame = async () => {
    setGameOver(false);
    setRound(0);
    successfulChoicesRef.current = 0;
    setShowFireworks(false);
    setIsGameReady(false);

    const sessionParams = await startSession("time-match-analog-to-digital");

    const sessionId = sessionParams.sessionId;
    if (sessionId) {
      sessionIdRef.current = sessionId;
      console.log("Session started with ID:", sessionId);
    }

    const currentLevel = sessionParams.level;
    setLevel(currentLevel);
    const params = JSON.parse(currentLevel.level_parameters);

    numberOfRoundsRef.current = params.numberOfRounds;
    timeBetweenRoundsRef.current = params.timeBetweenRounds;

    setNumberOfClocks(params.numberOfClocks);
    setShowSeconds(params.showSeconds);
    setIs24Hour(params.hour24);
    playClock.start();
    setTimeout(() => {
      setIsGameReady(true);
    }, 0);
  };

  useEffect(() => {
    if (!gameOver && isGameReady) {
      startNewRound();
    }
  }, [round, gameOver, isGameReady]);

  const startNewRound = () => {
    console.log("New round started");
    const now = new Date();
    const randomTime = (offset) => {
      const randomMinutes = Math.floor(Math.random() * 60);
      const randomSeconds = Math.floor(Math.random() * 60);
      return new Date(
        now.getTime() +
          offset * 60000 +
          randomMinutes * 60000 +
          randomSeconds * 1000
      );
    };

    // Target follows the clinician-defined progression, advancing as the
    // patient succeeds (success count = current step). Distractors stay random.
    const step = successfulChoicesRef.current;
    const newAnalogTime = targetTimeForStep(progression, step);
    setStartOfRoundTime(new Date(newAnalogTime));
    setAnalogTime(new Date(newAnalogTime));

    let times = [];
    for (let i = 0; i < numberOfClocks; i++) {
      times.push(randomTime(Math.floor(Math.random() * 180) - 120));
    }

    const matchIndex = Math.floor(Math.random() * numberOfClocks);
    times[matchIndex] = new Date(newAnalogTime);

    setClockTimes(times);
    setChecks(Array(numberOfClocks).fill(null));
  };

  const handleClockSelect = (index) => {
    const isCorrect =
      Math.abs(clockTimes[index].getTime() - startOfRoundTime.getTime()) < 1000;
    const newChecks = [...checks];
    newChecks[index] = isCorrect;
    setChecks(newChecks);

    if (isCorrect) {
      successfulChoicesRef.current = successfulChoicesRef.current + 1;
      if (correctSoundRef.current) {
        correctSoundRef.current.play().catch((error) => {
          console.error("Failed to play sound:", error);
        });
      }
    } else {
      if (errorSoundRef.current) {
        errorSoundRef.current.play().catch((error) => {
          console.error("Failed to play sound:", error);
        });
      }
    }

    // Last answer in: play time stops here, not after the pause between rounds.
    if (round + 1 >= numberOfRoundsRef.current) playClock.stop();
    setTimeout(() => {
      if (round + 1 >= numberOfRoundsRef.current) {
        setGameOver(true);
        if (successfulChoicesRef.current === numberOfRoundsRef.current) {
          setShowFireworks(true);
        }

        scoreRef.current =
          numberOfRoundsRef.current > 0
            ? (successfulChoicesRef.current / numberOfRoundsRef.current) * 100
            : 0;

        if (sessionIdRef.current) {
          endSession(sessionIdRef.current, scoreRef.current, playClock.elapsedMs());
        }
      } else {
        setRound(round + 1);
      }
    }, timeBetweenRoundsRef.current);
  };

  useEffect(() => {
    if (showFireworks) {
      fireworksAudioRef.current.loop = true;
      fireworksAudioRef.current.play().catch((error) => {
        console.error("Failed to play fireworks sound:", error);
      });
    } else {
      fireworksAudioRef.current.pause();
      fireworksAudioRef.current.currentTime = 0;
    }

    return () => {
      if (showFireworks) {
        fireworksAudioRef.current.pause();
        fireworksAudioRef.current.currentTime = 0;
      }
    };
  }, [showFireworks]);

  if (gameOver) {
    return (
      <GameLayoutTopbar
        infoPanel={
          <h2 className="heading-secondary">Analog To Digital Match - Game Over</h2>
        }
        gameArea={
          <GameOverScreen
            stats={[
              `Number of rounds: ${numberOfRoundsRef.current}`,
              `Number of successful choices: ${successfulChoicesRef.current}`,
              `Success percentage: ${scoreRef.current.toFixed(2)}%`,
            ]}
            onRestart={startNewGame}
            showFireworks={showFireworks}
            fireworksAnimation={fireworksData}
          />
        }
      />
    );
  }

  return (
    <GameLayoutTopbar
      infoPanel={
        <>
          <h2 className="heading-tertiary time-match-title">
            <span className="time-match-title__part">Analog To Digital &middot;</span>{" "}
            <span className="time-match-title__part">
              Level {level.level_number} &middot; Round {round + 1}/
              {numberOfRoundsRef.current}
            </span>
          </h2>
          <p className="text-muted time-match-hint">
            Choose the matching digital clock
          </p>
        </>
      }
      gameArea={
        <TimeMatchBoard
          questionKind="analog"
          questionTime={analogTime}
          optionTimes={clockTimes}
          checks={checks}
          onSelect={handleClockSelect}
          showSeconds={showSeconds}
          hour24={is24Hour}
        />
      }
    />
  );
};

export default AnalogToDigitalForm;
